/**
 * Shared types/helpers for the dynamic routine-inspection inputs used by the equipment
 * create and edit forms. Routine inspection fields are stored on the form as flat, computed
 * keys (`routineInspectionChecklistId_<rowNumber>` / `routineInspectionInterval_<rowNumber>`)
 * where rowNumber is the 1-based row position, so the number of rows can grow dynamically up
 * to EQUIPMENT_MAX_ROUTINE_INSPECTIONS.
 */

/** Maximum number of routine inspections that can be configured for a piece of equipment. */
export const EQUIPMENT_MAX_ROUTINE_INSPECTIONS = 6;

/** Routine-inspection portion of the equipment form data, keyed by 1-based row number. */
export interface RoutineInspectionFormFields {
  [key: `routineInspectionChecklistId_${number}`]: string | null | undefined;
  [key: `routineInspectionInterval_${number}`]: string | null | undefined;
}

export type RoutineFieldKind = 'checklistId' | 'interval';

/** Build the flat form-field name for a routine row's checklist or interval input. */
export function buildRoutineFieldName(kind: 'checklistId', rowNumber: number): `routineInspectionChecklistId_${number}`;
export function buildRoutineFieldName(kind: 'interval', rowNumber: number): `routineInspectionInterval_${number}`;
export function buildRoutineFieldName(kind: RoutineFieldKind, rowNumber: number) {
  return kind === 'checklistId'
    ? (`routineInspectionChecklistId_${rowNumber}` as const)
    : (`routineInspectionInterval_${rowNumber}` as const);
}

export interface IRoutineInspectionItem {
  checklistId: string;
  interval: string | null | undefined;
}

/**
 * Highest 1-based row that has a checklist selected, or 0 if none. Used to size the dynamic row
 * list so routines beyond EQUIPMENT_MAX_ROUTINE_INSPECTIONS (e.g. legacy/over-cap data) are still
 * rendered and collected rather than silently hidden and dropped on submit.
 */
export function getMaxPopulatedRoutineRow(formData: RoutineInspectionFormFields): number {
  return Object.entries(formData).reduce((max, [key, value]) => {
    const match = /^routineInspectionChecklistId_(\d+)$/.exec(key);
    return match && value ? Math.max(max, parseInt(match[1], 10)) : max;
  }, 0);
}

/**
 * Number of routine rows to render/scan: always at least the cap so empty rows are available to
 * add new routines, but expands to cover any extra existing routines beyond the cap.
 */
export function getRoutineRowCount(formData: RoutineInspectionFormFields): number {
  return Math.max(EQUIPMENT_MAX_ROUTINE_INSPECTIONS, getMaxPopulatedRoutineRow(formData));
}

/**
 * Collect the routine inspections the user has configured, in row order, keeping only rows
 * that have a checklist selected. The interval is carried through untouched (callers parse it).
 * Scans every rendered row (including any beyond the cap), so callers don't need to track the
 * visible row count — empty/cleared rows are simply filtered out.
 */
export function getRoutineItemsFromFormData(formData: RoutineInspectionFormFields): IRoutineInspectionItem[] {
  return Array.from({ length: getRoutineRowCount(formData) }, (_, i) => i + 1)
    .map((rowNumber) => ({
      checklistId: formData[buildRoutineFieldName('checklistId', rowNumber)],
      interval: formData[buildRoutineFieldName('interval', rowNumber)],
    }))
    .filter((item): item is IRoutineInspectionItem => !!item.checklistId);
}

/**
 * Whether the form has more routine inspections selected than allowed. The form intentionally
 * renders every existing routine (so over-cap data is visible/editable), but submission is gated
 * on the user trimming back down to EQUIPMENT_MAX_ROUTINE_INSPECTIONS.
 */
export function exceedsRoutineInspectionLimit(formData: RoutineInspectionFormFields): boolean {
  return getRoutineItemsFromFormData(formData).length > EQUIPMENT_MAX_ROUTINE_INSPECTIONS;
}

/** A persisted equipment routine, narrowed to the fields needed to seed the form. */
export interface ISeedableRoutine {
  deletedOn?: string | null;
  recurringDays: number;
  equipmentInspectionChecklistId?: string | null;
}

/** A persisted routine as needed to reconcile the form against the server (adds the record id). */
export interface IReconcilableRoutine extends ISeedableRoutine {
  id?: string | null;
}

/** The delete/create work needed to make an equipment's persisted routines match the form. */
export interface IRoutineReconciliation {
  /** Routine record ids to soft-delete — the user removed them or changed their interval. */
  routineIdsToDelete: string[];
  /** Form rows to create as new routine records — newly added, or an interval change. */
  routinesToCreate: IRoutineInspectionItem[];
}

/**
 * Parse a routine interval string into its day count. Uses `Number()` to match the `v_integers_only`
 * field validation exactly — `parseInt` would silently mis-read validation-passing inputs like
 * "1e2" (→ 1 instead of 100) or "0x10" (→ 0 instead of 16), persisting the wrong interval and
 * breaking reconciliation. Returns NaN for blank/invalid input.
 */
export function parseRoutineInterval(interval: string | null | undefined): number {
  return interval ? Number(interval) : NaN;
}

/** Whether a configured form row and a persisted routine are the same routine (checklist + interval). */
function isSameRoutine(item: IRoutineInspectionItem, routine: IReconcilableRoutine): boolean {
  return (
    item.checklistId === routine.equipmentInspectionChecklistId &&
    parseRoutineInterval(item.interval) === routine.recurringDays
  );
}

/**
 * Diff the user's configured routine rows against the equipment's persisted routines, so callers
 * delete only what was removed/changed and create only what is new/changed
 * Matches on checklist + interval; already-deleted routines are ignored, and persisted routines
 * missing an id can't be deleted so they are skipped.
 */
export function reconcileRoutineInspections(
  items: IRoutineInspectionItem[],
  persisted: readonly IReconcilableRoutine[] | null | undefined
): IRoutineReconciliation {
  const active = (persisted ?? []).filter((routine) => !routine.deletedOn);

  const routineIdsToDelete = active
    .filter((routine) => !!routine.id && !items.some((item) => isSameRoutine(item, routine)))
    .map((routine) => routine.id as string);

  const routinesToCreate = items.filter((item) => !active.some((routine) => isSameRoutine(item, routine)));

  return { routineIdsToDelete, routinesToCreate };
}

/**
 * Build the seed form fields for an equipment's existing routines (inverse of
 * getRoutineItemsFromFormData). Drops deleted routines and orders the rows by ascending interval,
 * so the fields render lowest-to-highest. Spread the result into the form's initial data.
 */
export function getRoutineFormData(
  routines: readonly ISeedableRoutine[] | null | undefined
): RoutineInspectionFormFields {
  return [...(routines ?? [])]
    .filter((routine) => !routine.deletedOn)
    .sort((a, b) => a.recurringDays - b.recurringDays)
    .reduce<RoutineInspectionFormFields>((acc, routine, index) => {
      const rowNumber = index + 1;
      return {
        ...acc,
        [buildRoutineFieldName('checklistId', rowNumber)]: routine.equipmentInspectionChecklistId ?? undefined,
        [buildRoutineFieldName('interval', rowNumber)]: routine.recurringDays.toFixed(),
      };
    }, {});
}
